/**
 * Money Green Dark Theme for Budget Flow
 *
 * Dark mode variant of the Money Green theme. Uses darker backgrounds
 * with green accents for a sophisticated, finance-focused dark UI.
 */

// oxlint-disable-next-line eslint/no-restricted-imports
import * as colorPalette from '@desktop-client/style/palette';

// Custom Money Green palette extensions
const moneyGreen = {
  50: '#f0fdf4',
  100: '#dcfce7',
  150: '#bbf7d0',
  200: '#86efac',
  300: '#4ade80',
  400: '#22c55e',
  500: '#16a34a',
  600: '#15803d',
  700: '#166534',
  800: '#14532d',
  900: '#052e16',
};

const moneyTeal = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  300: '#5eead4',
  400: '#2dd4bf',
  500: '#14b8a6',
  600: '#0d9488',
  700: '#0f766e',
  800: '#115e59',
  900: '#134e4a',
};

// Page backgrounds - deep dark green
export const pageBackground = '#0a1a0a';
export const pageBackgroundModalActive = '#0d1f0d';
export const pageBackgroundTopLeft = '#0a1a0a';
export const pageBackgroundBottomRight = '#0d1f0d';
export const pageBackgroundLineTop = '#1a3a1a';
export const pageBackgroundLineMid = '#0d1f0d';
export const pageBackgroundLineBottom = '#1a3a1a';
export const pageText = moneyGreen[100];
export const pageTextLight = moneyGreen[300];
export const pageTextSubdued = moneyGreen[600];
export const pageTextDark = moneyGreen[50];
export const pageTextPositive = moneyGreen[400];
export const pageTextLink = moneyTeal[400];
export const pageTextLinkLight = moneyTeal[300];

// Cards
export const cardBackground = '#0d1f0d';
export const cardBorder = moneyGreen[700];
export const cardShadow = '#000000';

// Tables
export const tableBackground = '#0d1f0d';
export const tableRowBackgroundHover = '#1a3a1a';
export const tableText = pageText;
export const tableTextLight = moneyGreen[400];
export const tableTextSubdued = moneyGreen[700];
export const tableTextSelected = moneyGreen[200];
export const tableTextHover = moneyGreen[100];
export const tableTextInactive = moneyGreen[500];
export const tableHeaderText = moneyGreen[300];
export const tableHeaderBackground = '#0d1f0d';
export const tableBorder = '#1a3a1a';
export const tableBorderSelected = moneyGreen[500];
export const tableBorderHover = moneyGreen[400];
export const tableBorderSeparator = moneyGreen[700];
export const tableRowBackgroundHighlight = '#1a3a1a';
export const tableRowBackgroundHighlightText = moneyGreen[200];
export const tableRowHeaderBackground = '#1a3a1a';
export const tableRowHeaderText = moneyGreen[100];

// Sidebar - Even darker green
export const sidebarBackground = '#050f05';
export const sidebarItemBackgroundPending = colorPalette.orange700;
export const sidebarItemBackgroundPositive = moneyGreen[600];
export const sidebarItemBackgroundFailed = colorPalette.red700;
export const sidebarItemBackgroundHover = '#0d1f0d';
export const sidebarItemAccentSelected = moneyGreen[400];
export const sidebarItemText = moneyGreen[300];
export const sidebarItemTextSelected = moneyGreen[300];
export const sidebarBudgetName = moneyGreen[200];

// Menus
export const menuBackground = '#0d1f0d';
export const menuItemBackground = '#1a3a1a';
export const menuItemBackgroundHover = '#264d26';
export const menuItemText = moneyGreen[100];
export const menuItemTextHover = menuItemText;
export const menuItemTextSelected = moneyGreen[400];
export const menuItemTextHeader = moneyGreen[500];
export const menuBorder = '#1a3a1a';
export const menuBorderHover = moneyGreen[600];
export const menuKeybindingText = moneyGreen[500];
export const menuAutoCompleteBackground = '#050f05';
export const menuAutoCompleteBackgroundHover = '#1a3a1a';
export const menuAutoCompleteText = moneyGreen[100];
export const menuAutoCompleteTextHover = moneyGreen[300];
export const menuAutoCompleteTextHeader = colorPalette.orange300;
export const menuAutoCompleteItemTextHover = menuAutoCompleteText;
export const menuAutoCompleteItemText = menuAutoCompleteText;

// Modals
export const modalBackground = '#0d1f0d';
export const modalBorder = '#1a3a1a';

// Mobile
export const mobileHeaderBackground = moneyGreen[800];
export const mobileHeaderText = moneyGreen[100];
export const mobileHeaderTextSubdued = moneyGreen[400];
export const mobileHeaderTextHover = 'rgba(200, 200, 200, .15)';
export const mobilePageBackground = '#0a1a0a';
export const mobileNavBackground = '#0d1f0d';
export const mobileNavItem = moneyGreen[600];
export const mobileNavItemSelected = moneyGreen[400];
export const mobileAccountShadow = '#000000';
export const mobileAccountText = moneyGreen[300];
export const mobileTransactionSelected = moneyGreen[600];
export const mobileViewTheme = mobileHeaderBackground;
export const mobileConfigServerViewTheme = moneyGreen[900];

// Markdown
export const markdownNormal = moneyGreen[700];
export const markdownDark = moneyGreen[400];
export const markdownLight = moneyGreen[800];

// Buttons - Menu style
export const buttonMenuText = moneyGreen[200];
export const buttonMenuTextHover = moneyGreen[100];
export const buttonMenuBackground = 'transparent';
export const buttonMenuBackgroundHover = 'rgba(74, 222, 128, .15)';
export const buttonMenuBorder = moneyGreen[600];
export const buttonMenuSelectedText = moneyGreen[900];
export const buttonMenuSelectedTextHover = moneyGreen[900];
export const buttonMenuSelectedBackground = moneyGreen[400];
export const buttonMenuSelectedBackgroundHover = moneyGreen[300];
export const buttonMenuSelectedBorder = buttonMenuSelectedBackground;

// Buttons - Primary (Money Green)
export const buttonPrimaryText = colorPalette.white;
export const buttonPrimaryTextHover = buttonPrimaryText;
export const buttonPrimaryBackground = moneyGreen[600];
export const buttonPrimaryBackgroundHover = moneyGreen[500];
export const buttonPrimaryBorder = buttonPrimaryBackground;
export const buttonPrimaryShadow = 'rgba(0, 0, 0, 0.5)';
export const buttonPrimaryDisabledText = moneyGreen[400];
export const buttonPrimaryDisabledBackground = moneyGreen[800];
export const buttonPrimaryDisabledBorder = buttonPrimaryDisabledBackground;

// Buttons - Normal
export const buttonNormalText = moneyGreen[100];
export const buttonNormalTextHover = buttonNormalText;
export const buttonNormalBackground = '#1a3a1a';
export const buttonNormalBackgroundHover = '#264d26';
export const buttonNormalBorder = moneyGreen[700];
export const buttonNormalShadow = 'rgba(0, 0, 0, 0.3)';
export const buttonNormalSelectedText = colorPalette.white;
export const buttonNormalSelectedBackground = moneyGreen[600];
export const buttonNormalDisabledText = moneyGreen[600];
export const buttonNormalDisabledBackground = '#1a3a1a';
export const buttonNormalDisabledBorder = moneyGreen[800];

// Calendar
export const calendarText = moneyGreen[100];
export const calendarBackground = '#050f05';
export const calendarItemText = moneyGreen[200];
export const calendarItemBackground = '#0d1f0d';
export const calendarSelectedBackground = moneyGreen[700];

// Buttons - Bare
export const buttonBareText = buttonNormalText;
export const buttonBareTextHover = moneyGreen[200];
export const buttonBareBackground = 'transparent';
export const buttonBareBackgroundHover = 'rgba(74, 222, 128, .1)';
export const buttonBareBackgroundActive = 'rgba(74, 222, 128, .2)';
export const buttonBareDisabledText = moneyGreen[600];
export const buttonBareDisabledBackground = buttonBareBackground;

// Notice/Success states
export const noticeBackground = moneyGreen[900];
export const noticeBackgroundLight = moneyGreen[800];
export const noticeBackgroundDark = moneyGreen[700];
export const noticeText = moneyGreen[300];
export const noticeTextLight = moneyGreen[400];
export const noticeTextDark = moneyGreen[200];
export const noticeTextMenu = moneyGreen[400];
export const noticeBorder = moneyGreen[700];

// Warning states
export const warningBackground = colorPalette.orange900;
export const warningText = colorPalette.orange300;
export const warningTextLight = colorPalette.orange400;
export const warningTextDark = colorPalette.orange200;
export const warningBorder = colorPalette.orange700;

// Error states
export const errorBackground = colorPalette.red900;
export const errorText = colorPalette.red300;
export const errorTextDark = colorPalette.red200;
export const errorTextDarker = colorPalette.red100;
export const errorTextMenu = colorPalette.red400;
export const errorBorder = colorPalette.red700;

// Upcoming/scheduled
export const upcomingBackground = moneyTeal[900];
export const upcomingText = moneyTeal[300];
export const upcomingBorder = moneyTeal[700];

// Forms
export const formLabelText = moneyGreen[400];
export const formLabelBackground = moneyGreen[800];
export const formInputBackground = '#1a3a1a';
export const formInputBackgroundSelected = '#264d26';
export const formInputBackgroundSelection = moneyGreen[600];
export const formInputBorder = moneyGreen[700];
export const formInputTextReadOnlySelection = '#1a3a1a';
export const formInputBorderSelected = moneyGreen[500];
export const formInputText = moneyGreen[100];
export const formInputTextSelected = moneyGreen[100];
export const formInputTextPlaceholder = moneyGreen[600];
export const formInputTextPlaceholderSelected = moneyGreen[500];
export const formInputTextSelection = moneyGreen[800];
export const formInputShadowSelected = moneyGreen[600];
export const formInputTextHighlight = moneyGreen[500];

// Checkboxes
export const checkboxText = moneyGreen[100];
export const checkboxBackgroundSelected = moneyGreen[600];
export const checkboxBorderSelected = moneyGreen[500];
export const checkboxShadowSelected = moneyGreen[700];
export const checkboxToggleBackground = moneyGreen[800];
export const checkboxToggleBackgroundSelected = moneyGreen[600];
export const checkboxToggleDisabled = moneyGreen[900];

// Pills/Tags
export const pillBackground = '#1a3a1a';
export const pillBackgroundLight = '#0d1f0d';
export const pillText = moneyGreen[200];
export const pillTextHighlighted = moneyGreen[400];
export const pillBorder = moneyGreen[700];
export const pillBorderDark = moneyGreen[600];
export const pillBackgroundSelected = moneyGreen[800];
export const pillTextSelected = moneyGreen[200];
export const pillBorderSelected = moneyGreen[500];
export const pillTextSubdued = moneyGreen[600];

// Reports/Charts
export const reportsRed = colorPalette.red400;
export const reportsBlue = colorPalette.blue400;
export const reportsGreen = moneyGreen[400];
export const reportsGray = colorPalette.gray500;
export const reportsLabel = moneyGreen[200];
export const reportsInnerLabel = moneyGreen[300];

// Notes/Tags
export const noteTagBackground = moneyGreen[800];
export const noteTagBackgroundHover = moneyGreen[700];
export const noteTagDefault = moneyGreen[800];
export const noteTagText = moneyGreen[200];

// Budget view
export const budgetCurrentMonth = tableBackground;
export const budgetOtherMonth = '#0a1a0a';
export const budgetHeaderCurrentMonth = budgetOtherMonth;
export const budgetHeaderOtherMonth = '#050f05';

// Floating action bar
export const floatingActionBarBackground = moneyGreen[700];
export const floatingActionBarBorder = moneyGreen[600];
export const floatingActionBarText = moneyGreen[100];

// Tooltips
export const tooltipText = moneyGreen[100];
export const tooltipBackground = '#0d1f0d';
export const tooltipBorder = moneyGreen[700];

// Calendar cells
export const calendarCellBackground = '#1a3a1a';

// Overlay
export const overlayBackground = 'rgba(0, 0, 0, 0.6)';
